import { isDomestic } from '@/utils/utils';
import { IFlightCredit, IFlightCreditAllocation } from '@/service/flightCredits';
import {
  CC_Charges,
  FET_Fee,
  FeeTypeIds,
  Handling_Fee,
  Segment_Fee,
} from '../enum';
import { ICharterLegSchema, ICharterOptionSchema, ICharterOtherFeeSchema } from '../form/schema';
import { ICharterFlightCreditApplied } from '../interface/ICreateCharterProposalRequest';
import { ILeg } from '../interface/ILeg';
import { ILegOptionFee } from '../interface/ILegOption';
import { ITripTaxSurcharge } from '../interface/ITripTaxSurcharge';

export const CalculatePreTaxOfOption = (option: ICharterOptionSchema) => {
  return option.legs
    .map((a: ICharterLegSchema) => {
      return Boolean(a.revenuePreTax) ? Number(a.revenuePreTax) : 0;
    })
    .reduce((a, b) => a + b, 0);
};

export const CalculateFlightCreditOfOption = (option: ICharterOptionSchema) => {
  return option.legs
    .map((a: ICharterLegSchema) => {
      return Boolean(a.flightCredit) ? Number(a.flightCredit) : 0;
    })
    .reduce((a, b) => a + b, 0);
};

/**
 * Remaining dollars available on an allocation, falling back to the original
 * allocation amount when the backend doesn't provide a remaining value.
 */
export const getAllocationRemainingDollars = (
  allocation: IFlightCreditAllocation | undefined
): number => {
  if (!allocation) {
    return 0;
  }
  return allocation.remainingDollars ?? allocation.dollars ?? 0;
};

/**
 * Dollars still available on a charter credit. Charter credits are a single dollar
 * pool spendable on any leg regardless of cabin, so this never keys off a leg's
 * aircraft type: it sums remaining across every allocation when the backend supplies
 * remaining values, otherwise falls back to the credit's single dollar limit (new
 * single-limit shape) or the sum of the allocations' original dollars (legacy shape).
 * Mirrors the charter handling in useFlightCreditData so display and consumption agree.
 */
export const getCreditRemainingDollars = (
  credit: IFlightCredit | null | undefined
): number => {
  if (!credit) {
    return 0;
  }
  const allocations = credit.allocations ?? [];
  const hasRemaining = allocations.some(
    (allocation) => allocation.remainingDollars !== undefined && allocation.remainingDollars !== null
  );
  if (hasRemaining) {
    return allocations.reduce(
      (sum, allocation) => sum + (allocation.remainingDollars ?? allocation.dollars ?? 0),
      0
    );
  }
  return credit.creditDollarLimit ?? allocations.reduce((sum, allocation) => sum + (allocation.dollars ?? 0), 0);
};

/**
 * Dollars of a credit already consumed by a specific trip, summed across its usages.
 * The backend nets these out of the credit's remaining, so refunding them exposes the
 * balance still editable by this trip.
 */
export const getCreditTripUsageDollars = (
  credit: IFlightCredit | null | undefined,
  tripId: number | null | undefined
): number => {
  if (!credit?.usages?.length || !tripId) {
    return 0;
  }
  return credit.usages.reduce(
    (sum, usage) => (usage.trip?.id === tripId ? sum + (usage.dollars ?? 0) : sum),
    0
  );
};

/**
 * Credit balance available to a specific trip: the backend remaining plus what this
 * trip has already applied (refunded). Lets a credit fully consumed by this trip still
 * surface as usable when re-editing its proposal.
 */
export const getCreditRemainingDollarsForTrip = (
  credit: IFlightCredit | null | undefined,
  tripId: number | null | undefined
): number => {
  return getCreditRemainingDollars(credit) + getCreditTripUsageDollars(credit, tripId);
};

/**
 * Remaining flight credit available to a single leg within an option.
 *
 * The backend nets this trip's already-saved usage out of the credit's remaining
 * balance, so `optionSavedDollars` (what this option previously applied) is added back
 * to recover the full editable pool — without it, re-opening a saved proposal would
 * show 0 and block editing. Options are independent alternatives, so only the option's
 * own saved usage and legs are considered. Sibling legs in the same option consume the
 * pool, so their currently-entered amounts are subtracted, leaving this leg free to
 * spend whatever is left. Negative/non-numeric sibling entries don't consume credit,
 * and the result is clamped at zero.
 */
export const calculateLegFlightCreditRemaining = (
  creditRemainingDollars: number,
  optionSavedDollars: number,
  optionLegAppliedDollars: number[],
  legIndex: number
): number => {
  const totalAvailable = creditRemainingDollars + optionSavedDollars;
  const othersApplied = optionLegAppliedDollars.reduce(
    (sum, dollars, index) =>
      index === legIndex || !Number.isFinite(dollars) || dollars < 0 ? sum : sum + dollars,
    0
  );
  return Math.max(0, totalAvailable - othersApplied);
};

/**
 * Distributes an option's available flight-credit pool across its legs in order, filling
 * each empty leg up to its own cost (revenue pre-tax) and spilling any leftover into the
 * next leg. Legs that already hold a value (saved usage or a manual edit) are preserved and
 * consume the pool but are never overwritten. Returns the new per-leg dollar amounts as
 * strings ('' for a leg that gets nothing), matching how the form stores `flightCredit`.
 */
export const computeOptionFlightCreditFill = (
  legCaps: number[],
  existingApplied: Array<string | number | null | undefined>,
  availablePool: number
): string[] => {
  let remaining = Number.isFinite(availablePool) ? Math.max(0, availablePool) : 0;
  return legCaps.map((rawCap, index) => {
    const existing = existingApplied[index];
    const hasExisting =
      existing !== null && existing !== undefined && String(existing).trim() !== '';
    if (hasExisting) {
      const existingValue = Number(existing);
      if (Number.isFinite(existingValue) && existingValue > 0) {
        remaining = Math.max(0, remaining - existingValue);
      }
      return String(existing);
    }
    const cap = Number.isFinite(rawCap) ? Math.max(0, rawCap) : 0;
    const fill = Math.max(0, Math.min(remaining, cap));
    remaining = Math.max(0, remaining - fill);
    const rounded = Number(fill.toFixed(2));
    return rounded > 0 ? String(rounded) : '';
  });
};

/**
 * Builds the per-leg flightCreditApplied payload for the charter proposal request.
 * Returns undefined for legs with no selected credit or no (zero) credit amount so
 * the field is omitted — the backend treats an absent value as "no credit on this leg".
 */
export const buildLegFlightCreditApplied = (
  flightCredit: string | number | null | undefined,
  credit: IFlightCredit | null | undefined
): ICharterFlightCreditApplied | undefined => {
  const dollars = Number(flightCredit);
  if (!credit || !Number.isFinite(dollars) || dollars <= 0) {
    return undefined;
  }
  return { flightCreditId: credit.id, dollars };
};

export const CalculateSegmentFees = (
  legs: Array<ILeg>,
  surcharge: Array<ITripTaxSurcharge>
) => {
  let fee = 0;
  const surchargeAmountNational = Number(
    GetSegmentFees(surcharge).DOMESTIC?.surchargeAmount
  );

  const surchargeAmountInterNational = Number(
    GetSegmentFees(surcharge).INTERNATIONAL?.surchargeAmount
  );

  for(let i = 0; i < legs.length; i++) {
    let isInternational = false;
    const leg = legs[i];
    if(!isDomestic(leg?.originAirport?.airportAddress?.country?.id, leg?.destinationAirport?.airportAddress?.country?.id)){
      isInternational = true;
    }
    fee += (leg.numberOfPassengers * (isInternational ? surchargeAmountInterNational : surchargeAmountNational));

  }
  return fee;
};

export const CalculateFETFees = (
  preTaxCost: number,
  handlingFee: number,
  internationalFee: number,
  otherFees: Array<ICharterOtherFeeSchema>,
  fetPercent: number
) => {
  // Calculate the other fees sum
  const otherFeeSum = otherFees
    .filter((a) => (a.taxable || a.special) && finiteOrZero(a.amount) > 0)
    .map((a) => finiteOrZero(a.amount))
    .reduce((a, b) => a + b, 0);
  return ((otherFeeSum + preTaxCost + handlingFee + internationalFee) * fetPercent) / 100;
};

export const CalculateAllInCashFees = (
  preTaxCost: number,
  handlingFee: number,
  segmentFee: number,
  fetAmount: number,
  internationalFee: number,
  otherFees: Array<ICharterOtherFeeSchema>
) => {
  // Calculate the other fees sum
  const otherFeeSum = otherFees
    .map((a) => finiteOrZero(a.amount))
    .reduce((a, b) => a + b, 0);

  return (otherFeeSum + preTaxCost + handlingFee + internationalFee + fetAmount + segmentFee);
};

/**
 * Net credit-card (surcharge-inclusive) total after a flight credit. Flight credit is a fare
 * reduction: it lowers the pre-surcharge fare (allInCash), and the credit-card surcharge then
 * applies to the REDUCED fare. So the net CC total is (allInCash - credit) * (allInCC / allInCash),
 * NOT allInCC - credit — subtracting the raw credit from the already-surcharged total overcharges
 * the client by credit * surcharge. The surcharge multiplier is derived from the option's own
 * allInCC / allInCash so it stays consistent with however allInCC was computed. Floored at zero.
 */
export const CalculateNetAllInCc = (
  allInCash: number,
  allInCC: number,
  flightCredit: number
): number => {
  const credit = finiteOrZero(flightCredit);
  if (credit <= 0) {
    return Math.max(0, finiteOrZero(allInCC));
  }
  if (!(allInCash > 0)) {
    // No positive fare to surcharge against: fall back to a plain reduction.
    return Math.max(0, finiteOrZero(allInCC) - credit);
  }
  const netFare = Math.max(0, allInCash - credit);
  return netFare * (allInCC / allInCash);
};

const finiteOrZero = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};


export const GetHandlingFees = (surcharges: Array<ITripTaxSurcharge>) => {
  return {
    CHARTER: surcharges.find(
      (surcharge) => surcharge.surchargeName === Handling_Fee.Handling_Fee_Charter
    ),
    JET_CARD: surcharges.find(
      (surcharge) => surcharge.surchargeName === Handling_Fee.Handling_Fee_DOM
    ),
  };
};

export const GetSegmentFees = (surcharges: Array<ITripTaxSurcharge>) => {
  return {
    DOMESTIC: surcharges.find(
      (surcharge) => surcharge.surchargeName === Segment_Fee.Segment_Fee_DOM
    ),
    INTERNATIONAL: surcharges.find(
      (surcharge) => surcharge.surchargeName === Segment_Fee.Segment_Fee_INT
    ),
  };
};

export const GetFETFees = (surcharges: Array<ITripTaxSurcharge>) => {
  return {
    DOMESTIC: surcharges.find(
      (surcharge) => surcharge.surchargeName === FET_Fee.FET
    ),
    INTERNATIONAL: surcharges.find(
      (surcharge) => surcharge.surchargeName === FET_Fee.FET
    ),
  };
};

export const GetCcCharge = (surcharge: Array<ITripTaxSurcharge>) => {
  return surcharge.find(
    (surcharge) => surcharge.surchargeName === CC_Charges.CC_Charges
  );
};

export const sumAmount = (array: number[]) => {
  if (!array || array?.length === 0) {
    return 0;
  } else {
    return (
      array?.reduce((total: number, num: number) => {
        return total + num;
      })
    );
  }
};

export const percentAmount = (amount: number, percentage: number) => {
  return amount * (percentage / 100);
};

export const calculateRevenueCost = (value: any, operatorCost: any) => {
  if(operatorCost === 0) {
    return {
      costInDollar: '0',
      revenuePreTax: '0',
      costInPercent: '0',
    };
  }
  let costInDollar = Number(value);
  let revenuePreTax = Number((operatorCost + costInDollar).toFixed(2));
  let costInPercent = Number(
    ((costInDollar / operatorCost) * 100).toFixed(2)
  );

  return { costInDollar, revenuePreTax, costInPercent };
};

export const getExistingFee = (fees: ILegOptionFee[]) => {
  const handlingFee = fees.find((fee) => String(fee.id) === FeeTypeIds.Handling_Fee)?.amount ?? 0;
  const segmentFee = fees.find((fee) => String(fee.id) === FeeTypeIds.Segment_Fee)?.amount ?? 0;
  const fetFee = fees.find((fee) => String(fee.id) === FeeTypeIds.FET_Fee)?.amount ?? 0;
  const internationalFee = fees.find((fee) => String(fee.id) === FeeTypeIds.International_Fee)?.amount ?? 0;
  const specialEventFee = fees.find((fee) => String(fee.id) === FeeTypeIds.Special_Event_Fee)?.amount ?? 0;
  return {
    handlingFee,
    segmentFee,
    fetFee,
    internationalFee,
    specialEventFee,
  };
};
